import EntityView from '@/se/components/entity/EntityView';
import GraphQLCRUD from '@/se/components/GraphQLCRUD';
import PWAInstallBanner from '@/se/components/PWAInstallBanner';
import { Accordion, FormControlLabel, Switch, Typography } from '@material-ui/core';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import { useApolloClient } from '@apollo/client';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import flow from 'lodash/fp/flow';
import get from 'lodash/fp/get';
import map from 'lodash/fp/map';
import update from 'lodash/fp/update';
import omit from 'lodash/omit';
import React from 'react';
import { Link } from 'react-router-dom';
import scheme, { refreshConfiguration, restoreHospital } from '../../../graph/hospitals';
import { withSession } from '@/state/Session';
import HospitalInput from '../../inputs/hospitals/HospitalInput';
import HospitalEditDialog from '../../inputs/hospitals/HospitalEditDialog';
import { unpackSessionObject } from '../../pages/unpackSessionObject';
import useDebugMode from '../../../hooks/useDebugMode';
import { allColumns, baseColumns, statusColumn } from './columns';
import HospitalDocumentUploader from './HospitalDocumentUploader';
import HospitalInstallation from './HospitalInstallation';
import HospitalMobileListItem from './HospitalMobileListItem';
import { OrganizationType } from './enums';

const Empty = { illustration: theme => theme.illustrations.rooms };

// Empty state for roles that cannot create organizations (Monitoring, GroupAdmin)
const ReadOnlyEmpty = {
  illustration: theme => theme.illustrations.rooms,
  title: 'No organizations assigned.',
  hint: 'Contact a SuperAdmin to be assigned to organizations.',
};

const VoidedFilter = ({ value, onChange }) => {
  const includeVoided = value?.includeVoided || false;
  return (
    <Box display="flex" alignItems="center" py={0.5}>
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={includeVoided}
            onChange={e => onChange({ ...value, includeVoided: e.target.checked })}
            color="primary"
          />
        }
        label={<Typography variant="body2">Show voided</Typography>}
      />
    </Box>
  );
};

// Base list config for read-only roles (Monitoring, GroupAdmin)
export const List = {
  columns: [...baseColumns],
  tableKey: 'Hospitals',
  Empty: ReadOnlyEmpty,
  MobileItemComponent: HospitalMobileListItem,
};

// SuperAdmin list config — includes voided toggle, status column, and row styling
export const SuperAdminList = {
  ...List,
  Empty,
  columns: [...baseColumns],
  FilterComponent: VoidedFilter,
  highlightedRowStyleProvider: item => (item.voidedAt ? { opacity: 0.5 } : undefined),
  queryOptionsProvider: props => ({
    variables: {
      includeVoided: props.filter?.includeVoided || false,
    },
    fetchPolicy: 'cache-and-network',
  }),
  mapListProps: props => ({
    columns: props.filter?.includeVoided ? [...baseColumns, statusColumn] : [...baseColumns],
  }),
};

const updateLinkedUsers = flow(update('monitors', map(get('id'))), update('groupAdmins', map(get('id'))));

const updateTimezone = update('timezone', timezone => (timezone && timezone.id ? timezone.id : timezone));

const setDefaultSessionTimeout = update('sessionTimeout', sessionTimeout => sessionTimeout || 10 * 60);

export const Create = {
  Input: HospitalInput,
  CreateComponent: HospitalEditDialog,
  Empty,
  prepareCreateData: flow(updateLinkedUsers, setDefaultSessionTimeout, updateTimezone),
  width: 'auto',
  height: '100%',
};

export const ShowActions = ({ data = {} }) => {
  const client = useApolloClient();
  const { isSuperAdmin, canBypassPermissions } = useDebugMode();
  const [busy, setBusy] = React.useState(false);
  const [message, setMessage] = React.useState(null);

  const handleRefresh = async () => {
    if (!data.id) {
      return;
    }
    try {
      setBusy(true);
      setMessage(null);
      await client.mutate({
        mutation: refreshConfiguration,
        variables: { id: parseInt(data.id, 10) },
      });
      setMessage('Configuration cache refreshed.');
    } catch (error) {
      console.error('Failed to refresh configuration cache', error);
      setMessage(error?.message || 'Failed to refresh configuration cache.');
    } finally {
      setBusy(false);
    }
  };

  const handleRestore = async () => {
    if (!data.id) {
      return;
    }
    try {
      setBusy(true);
      setMessage(null);
      await client.mutate({
        mutation: restoreHospital,
        variables: { id: parseInt(data.id, 10) },
        refetchQueries: ['hospitals', 'hospital'],
      });
      setMessage('Organization restored.');
    } catch (error) {
      console.error('Failed to restore organization', error);
      setMessage(error?.message || 'Failed to restore organization.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box display="flex" alignItems="center" gridGap="0.5rem">
      {message && (
        <Typography variant="body2" color="textSecondary">
          {message}
        </Typography>
      )}
      {isSuperAdmin && data.voidedAt && (
        <Button
          variant="outlined"
          onClick={handleRestore}
          disabled={busy}
          style={{ color: '#4caf50', borderColor: '#4caf50' }}
        >
          {busy ? 'Restoring…' : 'Restore Organization'}
        </Button>
      )}
      {canBypassPermissions && (
        <Button variant="outlined" color="primary" onClick={handleRefresh} disabled={busy}>
          {busy ? 'Refreshing…' : 'Refresh configuration cache'}
        </Button>
      )}
      <Button variant="contained" component={Link} to={`/su/${data.id}`}>
        Manage
      </Button>
    </Box>
  );
};

const ShowView = props => {
  const isSurgeryCenter = props.data?.type === OrganizationType.SurgeryCenter;
  const { isSuperAdmin } = useDebugMode();

  return (
    <div>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
          <Box flex={1} display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h2">General info</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <EntityView {...props} />
        </AccordionDetails>
      </Accordion>
      {isSurgeryCenter && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2a-content" id="panel2a-header">
            <Typography variant="h2">Uploads</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <HospitalDocumentUploader organizationId={props.data.id} />
          </AccordionDetails>
        </Accordion>
      )}
      {isSurgeryCenter && isSuperAdmin && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel3a-content" id="panel3a-header">
            <Typography variant="h2">Installation</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <HospitalInstallation hospital={props.data} />
          </AccordionDetails>
        </Accordion>
      )}
    </div>
  );
};

export const Show = {
  columns: allColumns,
  View: ShowView,
  fetchPolicy: 'network-only',
  Empty,
  Actions: ShowActions,
};

export const Edit = {
  Input: HospitalInput,
  EditComponent: HospitalEditDialog,
  Empty,
  prepareUpdateData: flow(updateLinkedUsers, setDefaultSessionTimeout, updateTimezone),
  width: 'auto',
  height: '100%',
};

const CRUD = GraphQLCRUD({
  entityName: 'Hospital',
  scheme,
  List: SuperAdminList,
  Create,
  Show,
  Edit,
});

const RU = GraphQLCRUD({
  entityName: 'Hospital',
  scheme: omit(scheme, 'remove'),
  List,
  Show,
});

const Hospitals = ({ isGroupAdmin }) => (
  <React.Fragment>
    <PWAInstallBanner />
    {isGroupAdmin ? <RU /> : <CRUD />}
  </React.Fragment>
);

export default withSession(unpackSessionObject)(Hospitals);
